import React from "react";
import { Helmet } from "react-helmet";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";

import { useAppTokens } from "../context/ThemeModeContext";

import iosAppQR from "../assets/images/readlinker_ios_app_download.png";
import androidAppQR from "../assets/images/readlinker_android_app_download.png";
import appStoreIcon from "../assets/images/appstore_us.svg";
import playStoreIcon from "../assets/images/playstore_us.png";

const AboutReadLinker: React.FC = () => {
  const TOKENS = useAppTokens();

  const commonImageStyle = { maxWidth: "50%", height: "auto" };
  const iconStyle = { maxWidth: "110px", marginRight: "12px" };

  const headingSx = {
    fontWeight: 900,
    letterSpacing: "-0.01em",
    color: TOKENS.text,
  } as const;

  const subHeadingSx = {
    fontWeight: 800,
    color: TOKENS.text,
    mt: 3,
  } as const;

  const bodySx = {
    color: TOKENS.textSub,
    lineHeight: 2,
  } as const;

  const glassCardSx = {
    bgcolor: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 3,
    p: { xs: 2.5, md: 4 },
    backdropFilter: "blur(6px)",
  } as const;

  const sectionDividerSx = {
    my: 3,
    borderColor: TOKENS.border,
  } as const;

  const listItemSx = {
    ...bodySx,
    mb: 0.5,
  } as const;

  return (
    <Box sx={{ mt: 7 }}>
    <>
      <Helmet>
        <title>ReadLinkerについて - ReadLinker</title>
        <meta
          name="description"
          content="ReadLinkerは、国立国会図書館の書誌データを基盤に、ISBNのない古い本も含めて蔵書を記録・発見できる書籍管理プラットフォームです。図書館情報学の目録概念に基づくデータベース構造と、本と典拠を繋ぐネットワーク表示「ReadLinker View」を特徴とします。"
        />
        <meta
          name="keywords"
          content="ReadLinker, 書籍管理, 本棚, リスト, 国立国会図書館, ISBNなし, 図書館情報学, FRBR, IFLA LRM, MARC, リンクトデータ, 典拠データ, ReadLinker View"
        />
      </Helmet>

      <Box
        sx={{
          minHeight: "calc(100vh - 56px)",
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          {/* Hero */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 900,
                letterSpacing: "-0.02em",
                lineHeight: 1.08,
                color: TOKENS.text,
              }}
            >
              ReadLinkerについて
            </Typography>

            <Typography variant="body1" sx={{ ...bodySx, fontSize: 18, lineHeight: 1.9 }}>
              図書館情報の発展の歴史と、紙の本の意味
            </Typography>
          </Box>

          {/* 1. なぜReadLinkerを作ったのか */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              なぜReadLinkerを作ったのか
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              既存の蔵書管理アプリの多くは、ISBNを手がかりに書誌を引きます。しかし日本でISBNの運用が始まったのは1981年で、それ以前の本にはそもそも番号がありません。古い本を持っている人ほど、蔵書アプリを使った管理コストは増大します。
              私は数ある蔵書アプリを試し、使い勝手の悪さを感じるなかで、ISBNをキーとしたデータベースには限界があると確信しました。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              国立国会図書館は、納本制度によって国内の出版物を網羅的に収集し、目録を作っています。ISBNのない本にも書誌データがあり、誰でも利用できる形で公開されています。ReadLinkerの開発は、国会図書館の蔵書管理システムを紐解く個人プロジェクトからスタートしました。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              正確な書誌データを揃えることで得られたのが、自分の蔵書を基にして、次に読むべき本へと、自然とつながる感覚でした。本から著者へ、著者からまた別の本へと辿った先で、名前も知らなかった著者の本に何冊も出会い、実際に買ってきました。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              ReadLinkerは、2023年に開発を開始した、紙の蔵書を管理するアプリです。2026年8月にバージョン3へアップデートし、新刊・近刊の書籍も登録できるようになりました。収録する図書（単行資料）の書誌データは約680万件で、一般ユーザー向けの蔵書管理アプリが扱う国内図書データベースとしては、最大級の網羅数です。
            </Typography>

            <Typography variant="body1" sx={bodySx}>
              ReadLinkerのデータベースの特徴は次の2点です。
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, mt: 1 }}>
              <Typography component="li" sx={listItemSx}>図書館情報学の目録概念に基づくデータベース構造</Typography>
              <Typography component="li" sx={listItemSx}>国立国会図書館のデータに基づく高い網羅率と、典拠の確かさ</Typography>
            </Box>
          </Paper>

          {/* 2. 図書館情報学とリンクトデータ */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              図書館情報学とリンクトデータ
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              目録という技術の歴史
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              本を整理する技術には長い歴史があります。今日、多くの公共図書館で使われている「十進分類法」は、1876年にメルヴィル・デューイが考案したデューイ十進分類法（DDC）に由来します。日本では、森清が1929年に日本十進分類法（NDC）を編纂し、現在は日本図書館協会が維持・改訂しています。ReadLinkerでは本棚の表示方法に、このNDCの目録を適用することができます。
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              「本」は4つの実体に分けられる
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              書誌メタデータをどう記述するかという問題は、デジタル時代に入り、セマンティック・ウェブの発達と並行して整理されてきました。その一つの到達点が、国際図書館連盟が1998年に公表したFRBR（書誌レコードの機能要件）です。FRBRは書誌の世界を実体・属性・関連としてモデル化し、
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
              <Typography component="li" sx={listItemSx}><b>著作（Work）</b>：知的・芸術的な創作そのもの</Typography>
              <Typography component="li" sx={listItemSx}><b>表現形（Expression）</b>：著作が言語・翻訳・演奏などの形で実現したもの</Typography>
              <Typography component="li" sx={listItemSx}><b>体現形（Manifestation）</b>：特定の版・刷として物理的に具体化したもの</Typography>
              <Typography component="li" sx={listItemSx}><b>個別資料（Item）</b>：手元にある、その1冊</Typography>
            </Box>
            <Typography variant="body1" paragraph sx={bodySx}>
              の4つを定義しました。FRBRは2017年に、典拠データ・件名典拠のモデル（FRAD、FRSAD）と統合されたIFLA LRM（IFLA図書館参照モデル）へと発展しています。日本では、この系譜を受けた『日本目録規則2018年版』が国立国会図書館などで適用されています。
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              MARC、そしてリンクトデータへ
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              書誌データを機械可読な形式で流通させる仕組みがMARC（MAchine-Readable Cataloging）です。1960年代に米国議会図書館のヘンリエット・アヴラムらが開発し、1971年に米国標準、1973年に国際標準となりました。日本では国立国会図書館が作成するJAPAN/MARCがこれにあたり、現在はJAPAN/MARC MARC21フォーマット（ISO 2709）で提供されています。
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              ただしMARCは、1レコードの中に情報を詰め込む「レコード形式」です。データ形式としては機械判読に向いており、必ずしも人間にとって読みやすいわけではなく、データ転送の要件などにもやや過適合した、当時の図書館のアーキテクチャに密に結びついた形式だと言えます。
              図書館はその後、「書誌」自体のデータを記述するに留まらず、世界の様々な事象を結びつけていくという考え方にシフトしました。これをデータ表現に落とし込む取り組みが、RDFというリンクトデータ形式です。米国議会図書館がMARCの後継として設計しているBIBFRAMEはRDFで記述されていますし、国立国会図書館も書誌データをDC-NDL（RDF）形式で、典拠データをRDF／SPARQLで提供しています。なお、ReadLinkerでは全書誌データを対象とするため、MARC形式の書誌データを元にデータベースを構築していますが、将来拡張されるリンクトデータの構造を前提として開発を進めています。
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              リンクトデータの形で管理される書誌データ・典拠データで実装される図書館のデータ管理の実装は、以上のような歴史の中で、洗練されてきました。知識の管理と活用という課題に対する解決策を探す者に対して、図書館情報は大きなヒントを与えてくれます。
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              LLMとグラフデータベース
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              近年、LLMがデータを活用する文脈で、グラフデータベースが再評価されています。複数のオブジェクトが「どういう意味づけで関連しているか」というメタデータをシステム内の共通語彙として持ち、それで管理することで、人にとってわかりやすく有機的な利用が可能になりますが、これはLLMにとっても同様に、使い勝手が良いからです。
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              図書館情報学は、インターネットが今日のように発達する以前から、膨大な資料を正しく管理するという実需に応えるために、実体と関連によるモデル化を先行して進めてきました。図書館情報学に基づいて構築された書誌メタデータベースは、その起源から、LLMとの相性が良いはずです。ReadLinkerは人にとってもLLMにとっても使える書誌データのプラットフォームを目指し、APIの整備を進めます。
            </Typography>
          </Paper>

          {/* 3. データの出所と網羅性 */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              データの出所と網羅性
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              ISBNのない本
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              日本でISBNの運用が始まったのは1981年（日本図書コード）で、書籍JANコードの実運用は1990年、13桁化は2007年です。したがって、1980年代以前に出版された本の多くにはISBNがありません。ISBNは付与が義務ではないため、現在でも自費出版物や官公庁刊行物などISBNを持たない本が存在します。
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              多くの蔵書管理アプリはISBNを主キーとして書誌を引くため、こうした本は「検索しても出てこない本」になります。こうした本に対応するために、蔵書アプリの多くはユーザーによる自主登録を認めています。しかし、ユーザーの入力コスト、入力ミスは、無視できません。ReadLinkerは国立国会図書館の書誌データを基盤にすることで、ISBNのない本も登録でき、典拠情報も参照されます。古い本や非流通の本を多く持つ人ほど、この意味を実感できると思います。
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              典拠データ
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              著者名や件名などを整理・同定したデータを「典拠」と呼びます。ReadLinkerは典拠データの一部をアプリ上でそのまま利用しており、同姓同名の著者の区別や、旧字・異体字・別名義の名寄せに使っています。ReadLinkerのグラフページは、この典拠データと書誌データから構築されています。
            </Typography>
          </Paper>

          {/* 4. 何を管理対象とするか */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              何を管理対象とするか
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              現時点でReadLinkerが管理対象とするのは、国内で出版された図書（単行資料）です。これは暫定的な線引きですが、以下の課題を認識しています。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              <b>雑誌（逐次刊行物）について。</b>国立国会図書館は雑誌も収集・目録化しており、雑誌記事索引も提供しています。しかし逐次刊行物は、タイトル単位の書誌と号単位の所蔵とを分けて扱う必要があり、図書とはデータモデルが大きく異なります。図書と同じ画面・同じ粒度で見せる方法は模索中であり、ReadLinkerではまだ満足のいく形を提供できていません。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              <b>独自資料について。</b>同人誌、私家版、配布物など、全国書誌に収録されない資料をユーザー自身が登録できるようにすべきか、という問題があります。技術的には可能ですが、他の図書とデータの粒度・信頼性が揃わないまま混ぜると、逆にユーザー体験を損ねる可能性があります。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              <b>書籍以外の資料について。</b>ReadLinkerの射程が「本棚にあるすべてのもの」であるなら、CDや写真アルバムのような個人的な資料も対象になり得ます。これらは扱わない予定です。
            </Typography>

            <Typography variant="body1" sx={bodySx}>
              <b>電子書籍・PDFについて。</b>ReadLinkerでも、バージョン2ではKindleの管理機能を実装しましたが、その後対応を中止しました。電子媒体にはすでに複数の専用の優れた管理ツールが存在しています。
            </Typography>
          </Paper>

          {/* 5. つながりで本に出会う——ReadLinker View */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              つながりで本に出会う——ReadLinker View
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              ReadLinkerには、本と典拠を結んだネットワークを描く画面があります。Web版のグラフページと、モバイルアプリの「ReadLinker View」画面です。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              描いているのは、本と、その本に紐づく典拠を結んだネットワークです。著者や、訳者や、「その本が論じている人」などが、同じ一本の線として扱っています。これにより、ある人物について書かれた本と、その人物自身が書いた本が繋がります。
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={subHeadingSx}>
              橋になる本
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              グラフビューを開くと、典拠を中心に本が房のように集まった、いくつかの島が見えます。多くの本は、一つの典拠にだけ繋がった葉です。
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              面白いのは、複数の典拠に同時に繋がっている本です。二つの島の間に架かるそうした本により、それまで名前も知らなかった著者の領域に目が向きます。開発者自身、この橋を渡って多くの本と出会い、購入してきました。ぜひこの書籍収集体験の面白さを感じていただければと思います。
            </Typography>
            <Typography variant="body1" paragraph sx={bodySx}>
              「ReadLinker」という名前の由来の一つは、この機能です。
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              ReadLinkerの本質はデータベースにあると考えていますが、その可視化手法については、ユーザーの皆様の意見を取り入れながらブラッシュアップを続けます。
            </Typography>
          </Paper>

          {/* 6. 本の発展は文明の発展と共にある */}
          <Paper elevation={0} sx={{ ...glassCardSx, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              本の発展は文明の発展と共にある
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              本には、著者が時に一生をかけて収集し、吟味し、研ぎ澄ませた言明が詰まっています。私たち人類は、本を通じて知的活動の成果を次の世代に受け渡すことで、科学的知識を含む説明的知識を発達させ、今日の文明を築き上げました。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              無数の本の中には、真実に近い言明も、真実からかけ離れた言明もあります。ただし、いかなる言明であれ、紙の本として印刷され流通した以上、それは事実上、不特定多数による批判に晒されます。知識が育つのは、批判が可能な場所においてです。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              紙の本という媒体は、書くにも、届けるにも、保管するにも、相応のコストがかかります。このコストはしかし悪いことばかりではありません。「速いメディア」の負の側面を、紙の本は構造的に免れています。インターネットが発達し、SNSが生活に浸透した時代において、紙の本が知識の媒体として持つ優位性は明確に存在します。
            </Typography>

            <Typography variant="body1" paragraph sx={bodySx}>
              SNSの発達と書籍の相対的な地位の低下は、真実からかけ離れた情報の拡散を助け、綿密に練られた良い説明（往々にして、その伝達には高いコストがかかります）の居場所を圧迫し、間接的に、文明社会を支える靭帯の一つである、「真実への信頼」あるいは「世界はより良く出来るという期待」を毀損しています。この傾向を食い止める方策の一つが、紙の本の復権だと考えます。
            </Typography>

            <Typography variant="body1" sx={bodySx}>
              ReadLinkerは、紙の本の持つ魅力を引き出し、読書体験と蔵書管理体験を向上させます。このアプリを通じ、開かれた自由社会と人類の叡智の発展に少しでも貢献することを目指します。
            </Typography>
          </Paper>

          {/* Download section */}
          <Paper elevation={0} sx={glassCardSx}>
            <Typography variant="h5" component="h2" gutterBottom sx={headingSx}>
              アプリをダウンロード
            </Typography>

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 900, mb: 1, color: TOKENS.text, letterSpacing: "-0.01em" }}
                >
                  iOS
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <a
                    href="https://apps.apple.com/jp/app/readlinker/id6477534373"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={appStoreIcon} alt="App Store Icon" style={iconStyle} />
                  </a>
                  <img src={iosAppQR} alt="ReadLinker iOS App Download" style={commonImageStyle} />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 900, mb: 1, color: TOKENS.text, letterSpacing: "-0.01em" }}
                >
                  Android
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <a
                    href="https://play.google.com/store/apps/details?id=jp.ada_llc.readlinker2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={playStoreIcon} alt="Google Play Store Icon" style={iconStyle} />
                  </a>
                  <img
                    src={androidAppQR}
                    alt="ReadLinker Android App Download"
                    style={commonImageStyle}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </>
    </Box>
  );
};

export default AboutReadLinker;
